// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import 'app/common/react/components/growth/activation/NuxEducationLoader/NuxEducationLoader.scss';
import i18n from 'app/common/lib/i18n';
import NuxEducationLoaderGrid from 'app/common/react/components/growth/activation/NuxEducationLoader/NuxEducationLoaderGrid';
import NuxEducationLoaderPin from 'app/common/react/components/growth/activation/NuxEducationLoader/NuxEducationLoaderPin';
import ProgressBar from 'app/common/react/components/growth/activation/NuxModal/ProgressBar';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import withViewer from 'app/common/lib/react/withViewer';
import { track } from 'app/common/lib/react/analytics';
import setNuxProgress, { type NuxStep } from 'app/common/redux/actions/nuxProgress';
import {
  type InterestObj,
  NUX_LOADING_STEP_STATUS,
  SHOWED,
  COMPLETED,
} from 'app/common/react/components/growth/activation/NuxConst/NuxConst';
import { Box, Heading } from 'gestalt';
import { compose, type Dispatch } from 'redux';

const PROGRESS_UPDATE_DURATION = 300;
const LOADING_STEP_DURATION = 5100;
const INCREMENT_NUX_STEP_DELAY = 1000;

type OwnProps = {|
  incrementNuxStep: Function,
  isRenux: boolean,
  selectedInterests: Array<InterestObj>,
  showProgressBar?: boolean,
|};

type HOCProps = {|
  viewer: AuthViewer,
|};

type DispatchProps = {|
  dispatchSetNuxStepProgress: (NuxStep) => void,
|};

type ConnectProps = {|
  ...DispatchProps,
  ...OwnProps,
|};

type NuxEducationLoaderProps = {|
  ...DispatchProps,
  ...HOCProps,
  ...OwnProps,
|};

type State = {|
  progressPercent: number,
|};

export class NuxEducationLoader extends PureComponent<NuxEducationLoaderProps, State> {
  state = {
    progressPercent: 0,
  };

  componentDidMount() {
    const { dispatchSetNuxStepProgress, isRenux } = this.props;
    if (!isRenux) {
      const userStateResource = ResourceFactory.create('UserStateResource', {
        state: NUX_LOADING_STEP_STATUS,
        value: SHOWED,
      });
      userStateResource.callCreate();
    }
    dispatchSetNuxStepProgress('loader');
    track(PCONST.thrift.EventType.VIEW, {
      view: isRenux ? PCONST.thrift.ViewType.REDO_ORIENTATION : PCONST.thrift.ViewType.ORIENTATION,
      viewParameter: PCONST.thrift.ViewParameterType.ORIENTATION_LOADING_HOME_FEED,
    });
    this.intervalId = setInterval(
      () =>
        this.setState((prevState) => ({
          progressPercent: Math.min(
            prevState.progressPercent + 100 / (LOADING_STEP_DURATION / PROGRESS_UPDATE_DURATION),
            100,
          ),
        })),
      PROGRESS_UPDATE_DURATION,
    );
  }

  componentDidUpdate(prevProps: NuxEducationLoaderProps, prevState: State) {
    if (this.state.progressPercent >= 100) {
      clearInterval(this.intervalId);
      setTimeout(this.incrementStepAndCompleteLoading, INCREMENT_NUX_STEP_DELAY);
    }
  }

  componentWillUnmount() {
    clearInterval(this.intervalId);
  }

  intervalId: IntervalID;

  incrementStepAndCompleteLoading = () => {
    const { incrementNuxStep, isRenux } = this.props;
    incrementNuxStep();
    if (!isRenux) {
      const userStateResource = ResourceFactory.create('UserStateResource', {
        state: NUX_LOADING_STEP_STATUS,
        value: COMPLETED,
      });
      userStateResource.callCreate();
    }
  };

  render() {
    const { isRenux, selectedInterests, showProgressBar } = this.props;
    const { progressPercent } = this.state;
    return (
      <div className="NuxEducationLoader" data-test-id="nux-complete-loader">
        <Box
          alignItems="center"
          display="flex"
          direction="row"
          height="100%"
          justifyContent="center"
          overflow="hidden"
          width="100%"
        >
          <Box position="relative" marginLeft={10} marginRight={4}>
            <div className="NuxEducationLoader__loadingCopyContainer">
              <Heading size="sm">{i18n._('All set!', 'Title for nux loading step')}</Heading>
              <Box marginTop={4}>
                {isRenux
                  ? i18n._(
                      "We're refreshing your home feed with lots of new Pins...",
                      'Details for ReNUX loading step',
                    )
                  : i18n._(
                      "We're building your home feed with lots of Pins...",
                      'Details for nux loading step',
                    )}
              </Box>
            </div>
            <div className="NuxEducationLoader__pinEducationCopyContainer">
              <Heading size="sm">
                {i18n._('Pins are ideas.', 'Pin education on NUX loading step')}
              </Heading>
              <Box marginTop={5}>
                {i18n._(
                  'They are links saved from around the web',
                  'Pin education on NUX loading step',
                )}
              </Box>
            </div>
          </Box>
          <Box marginRight={5} marginLeft={5} width={344}>
            <NuxEducationLoaderGrid />
          </Box>
          <Box>
            <NuxEducationLoaderPin selectedInterest={selectedInterests[0]} />
          </Box>
        </Box>
        {!showProgressBar ? (
          <Box position="absolute" top width="100%">
            <ProgressBar height={8} color="#e60023" progress={progressPercent} />
          </Box>
        ) : null}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  dispatchSetNuxStepProgress: (step: NuxStep) => dispatch(setNuxProgress(step)),
});

export default compose(
  connect<ConnectProps, OwnProps, _, DispatchProps, _, _>(
    null,
    mapDispatchToProps,
  ),
  withViewer,
)(NuxEducationLoader);
